import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Row, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { deleteGuardian, deleteMember, postNote, patchCase, patchGuardian, patchMember } from "../../services/httpClient";
import { User } from "../../types/user";
import ExitNote from "./ExitNote";
import { Member } from "../../types/member";

interface DeleteState {
  setIsDeleting: any;
  user: User | Member;
  family: any[];
  fetchHousehold: any;
  caseStatus: string;
  getCase: any;
  onDone: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const ConfirmDelete = ({
  setIsDeleting,
  user,
  family,
  fetchHousehold,
  onDone,
  caseStatus,
  getCase,
  setShowToast,
  setToastText,
  setToastBody,
}: DeleteState) => {
  const { id } = useParams();
  const mainRef = useRef();
  const [exitDate, setExitDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [exitNote, setExitNote] = useState<string>();
  const [exitType, setExitType] = useState<string>("voluntary");
  const [newMainContact, setNewMainContact] = useState<string>("");
  const name = user.firstname + " " + user.lastname;
  const isLastUser = family.length === 1;
  const isPrimary = family.find((g: any) => g.id === user?.id)?.is_primary;
  const reassignMainContact = !!(
    isPrimary && (family?.filter((g: any) => g.id !== user?.id && g.role === "family" && !g.deleted_at).length || 0) > 0
  );

  const mainContactOptions = family
    .filter((g: any) => g.id !== user?.id && g.role === "family" && !g.deleted_at && !g.is_banned)
    .map((g: any) => ({
      id: g.id,
      key: g.id,
      name: `${g.firstname} ${g.lastname}`,
    }));

  const handleScroll = (ref: any) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "instant", block: "end", inline: "nearest" });
    }
  };

  useEffect(() => {
    handleScroll(mainRef);
  }, []);

  const onClickDeleteFinalWExit = () => {
    const deleteUserAndSaveNote = () => {
      if (user.role === "family") {
        const note = {
          notetype: "exit",
          subject: {
            id: user?.id,
            type: "guardian",
          },
          fields: {
            exit_type: exitType,
            description: exitNote,
            date: new Date().toISOString().split("T")[0],
          },
        };

        //save exit note
        postNote(note, id)
          .then((res: any) => {
            const note_id = res[0].data.note_id;

            const exit = {
              exit_at: exitDate,
              exit_note_id: note_id,
            };

            deleteGuardian(id, user?.id, exit)
              .then((res: any) => {
                setShowToast(true);
                setToastText("Member Deleted");
                setToastBody(name);
                onDone();
                //delay
                setTimeout(() => {
                  setIsDeleting(false);
                }, 1000);
              })
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });
      } else {
        const note = {
          notetype: "exit",
          subject: {
            id: user?.id,
            type: "member",
          },
          fields: {
            exit_type: exitType,
            description: exitNote,
            date: new Date().toISOString().split("T")[0],
          },
        };

        //save exit note
        postNote(note, id)
          .then((res: any) => {
            const note_id = res[0].data.note_id;

            const exit = {
              exit_at: exitDate,
              exit_note_id: note_id,
            };

            deleteMember(id, user?.id, exit)
              .then((res: any) => {
                setShowToast(true);
                setToastText("Member Deleted");
                setToastBody(name);
                onDone();
                //delay
                setTimeout(() => {
                  setIsDeleting(false);
                }, 1000);
              })
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    };

    // reassign main contact
    if (user?.role === "family" && isPrimary && newMainContact) {
      const updated = { is_primary: true };
      patchGuardian(updated, id, newMainContact)
        .then(() => deleteUserAndSaveNote())
        .catch((err: any) => {
          console.log(err);
          deleteUserAndSaveNote();
        });
    } else {
      deleteUserAndSaveNote();
    }
  };

  const onClickDeleteFinal = () => {
    const deleteUser = () => {
      if (user.role === "family") {
        deleteGuardian(id, user?.id)
          .then((res: any) => {
            setShowToast(true);
            setToastText("Member Deleted");
            setToastBody(name);
            onDone();
            //delay
            setTimeout(() => {
              setIsDeleting(false);
            }, 1000);
          })
          .catch((err: any) => {
            console.log(err);
          });
      } else {
        deleteMember(id, user?.id)
          .then((res: any) => {
            setShowToast(true);
            setToastText("Member Deleted");
            setToastBody(name);
            onDone();
            //delay
            setTimeout(() => {
              setIsDeleting(false);
            }, 1000);
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    };

    // reassign main contact
    if (user?.role === "family" && isPrimary && newMainContact) {
      const updated = { is_primary: true };
      patchGuardian(updated, id, newMainContact)
        .then(() => deleteUser())
        .catch((err: any) => {
          console.log(err);
          deleteUser();
        });
    } else {
      deleteUser();
    }
  };

  const onClickDeactivateCase = async () => {
    // Deactivate the case
    await patchCase({ casestatus: "inactive" }, id);

    // create exit note for the current user
    if (caseStatus === "active") {
      const note = {
        notetype: "exit",
        subject: {
          id: user?.id,
          type: user?.role === "family" ? "guardian" : "member",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };
      const res = await postNote(note, id);
      const noteId = res[0].data.note_id;

      const exitData = {
        exit_at: exitDate,
        exit_note_id: noteId,
      };

      if (user?.role === "family") {
        await patchGuardian(exitData, id, user?.id);
      } else {
        await patchMember(exitData, id, user?.id);
      }
    }

    setShowToast(true);
    setToastText("Case Deactivated");
    setToastBody(`Case has been made inactive`);
    fetchHousehold();
    getCase();
    setTimeout(() => {
      setIsDeleting(false);
    }, 1000);
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
  };

  return (
    <>
      <Card className="bg-light mb-3">
        <Card.Body>
          {isLastUser ? (
            <>
              <Stack direction="horizontal" gap={3} className={caseStatus === "active" ? "mb-3" : ""}>
                <Stack className="justify-content-center">
                  <Card.Subtitle>
                    {"You cannot delete the last person. "}
                    {caseStatus === "active" && "Would you like to change case status to Inactive?"}
                  </Card.Subtitle>
                </Stack>
                {caseStatus === "active" && (
                  <>
                    <Button className="app-danger-color button text-nowrap" onClick={onClickDeactivateCase}>
                      Deactivate Case
                    </Button>
                    <div className="vr" />
                  </>
                )}
                <Button className="app-primary-color button" onClick={onClickCancelDelete}>
                  Cancel
                </Button>
              </Stack>
              {caseStatus === "active" && (
                <ExitNote
                  exitDate={exitDate}
                  setExitDate={setExitDate}
                  exitNote={exitNote}
                  setExitNote={setExitNote}
                  exitType={exitType}
                  setExitType={setExitType}
                  isBanned={false}
                  setIsBanned={() => {}}
                  reassignMainContact={reassignMainContact}
                  newMainContact={newMainContact}
                  setNewMainContact={setNewMainContact}
                  mainContactOptions={mainContactOptions}
                />
              )}
            </>
          ) : (
            <>
              <Stack direction="horizontal" gap={3}>
                <Stack style={{ justifyContent: "center" }}>
                  <Card.Subtitle>Are you sure you want to delete {name}?</Card.Subtitle>
                </Stack>
                <Button
                  className="app-danger-color button"
                  onClick={() => {
                    caseStatus === "active" ? onClickDeleteFinalWExit() : onClickDeleteFinal();
                  }}
                >
                  Confirm Delete
                </Button>
                <div className="vr" />
                <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
                  Cancel
                </Button>
              </Stack>
              {caseStatus === "active" && (
                <>
                  <div className="mt-3"></div>
                  <ExitNote
                    exitDate={exitDate}
                    setExitDate={setExitDate}
                    exitNote={exitNote}
                    setExitNote={setExitNote}
                    exitType={exitType}
                    setExitType={setExitType}
                    isBanned={false}
                    setIsBanned={() => {}}
                    reassignMainContact={reassignMainContact}
                    newMainContact={newMainContact}
                    setNewMainContact={setNewMainContact}
                    mainContactOptions={mainContactOptions}
                  />
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      <Row ref={mainRef} className="mb-5"></Row>
    </>
  );
};

export default ConfirmDelete;
